import { theme } from './theme';

// formatTime — shared by DealScreen.js and FreezeDetailScreen.js.
// Previously duplicated per-file (and briefly, only locally scoped
// inside DealScreen's own component body), which caused a real
// production crash the moment ItineraryLeg — a separate top-level
// component — tried to use it without closure access. One shared
// module-level copy now, not a per-file convention to keep back in sync.
export function formatTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// formatDate — companion to formatTime (Sep 23, per Ali's report: the
// itinerary card showed times with no date, so a Day-0 check on Sep 22
// could be showing a flight departing anywhere from tomorrow to months
// out with nothing on screen to tell them apart). Same shared-module
// reasoning as formatTime above: used by both DealScreen.js and
// FreezeDetailScreen.js, defined once.
export function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ItineraryLeg — renders one leg (outbound or return) as its real
// segment-by-segment chain instead of collapsing a connection down to
// just its first flight number (the actual bug this was built to fix:
// a connecting itinerary's flight_number used to only ever reflect the
// FIRST segment, silently dropping the rest of the journey). Falls
// back to the old single-block view when `segments` isn't present
// (older cached pin/freeze data from before this fix — self-heals the
// next time that pin gets re-checked and its flight JSON gets
// overwritten with the new shape).
export function ItineraryLeg({ label, flight }) {
  const segments = flight.segments && flight.segments.length > 0 ? flight.segments : [flight];

  return (
    <div style={{ marginBottom: '4px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <div style={{ fontSize: '9.5px', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </div>
        {/* fare_brand is a whole-leg attribute (Duffel's fare_brand_name
            lives on the slice, not each individual segment) — shown
            once per leg here, not repeated per segment below. Found
            from a live comparison where FlyAtlast's price sat between
            Google Flights' published "Basic" and "Standard" tiers for
            the identical flights — this makes which real fare bucket
            was actually quoted a checkable fact instead of a guess. */}
        {flight.fare_brand && (
          <span style={{ fontSize: '9px', fontWeight: 800, color: theme.textMuted, background: theme.bg, padding: '2px 7px', borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            {flight.fare_brand}
          </span>
        )}
      </div>
      {segments.map((seg, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12.5px', fontWeight: 700, color: theme.structural, marginBottom: '4px' }}>
            <span>{seg.airline} · {seg.flight_number}</span>
          </div>
          {/* Date sits right next to the time, on both ends of the leg
              (Sep 23) — departure and arrival can be different days on
              an overnight or long-haul flight, so one date at the top
              of the card isn't enough. Replaces the old "+1" superscript
              (which only said arrival was a day later, not which day) —
              an explicit date is strictly more information, so it's not
              kept alongside. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', marginBottom: i < segments.length - 1 ? '2px' : '10px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: theme.textPrimary }}>{formatTime(seg.departure_time)}</span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: theme.textMuted }}>{formatDate(seg.departure_time)}</span>
              </div>
              <div style={{ color: theme.textMuted }}>{seg.departure_airport}</div>
            </div>
            <div style={{ flex: 1, height: '1px', background: theme.border }} />
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: theme.textPrimary }}>{formatTime(seg.arrival_time)}</span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: seg.next_day ? theme.action : theme.textMuted }}>{formatDate(seg.arrival_time)}</span>
              </div>
              <div style={{ color: theme.textMuted }}>{seg.arrival_airport}</div>
            </div>
          </div>
          {i < segments.length - 1 && (
            <div style={{ fontSize: '11.5px', color: theme.textMuted, fontStyle: 'italic', padding: '0 0 10px 4px' }}>
              Layover in {seg.arrival_airport}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}